import React from 'react';
import { Box, Button, IconButton, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';

const MAX_CO_OWNERS = 30;

/**
 * Owner, co-owners, municipality, cadastral municipality, property sheet (list nepokretnosti) and parcel,
 * all optional free text (used on jobs and quotes). `client` is only offered as a name suggestion,
 * never filled in by itself.
 */
export default function SurveyFields({ values, client, onChange }) {
    const { t } = useTranslation();
    const coOwners = values.coOwners || [];
    const field = (key, label, extra = {}) => (
        <TextField fullWidth size="small" label={label} value={values[key] || ''}
                   onChange={(e) => onChange({ [key]: e.target.value })} {...extra} />
    );
    const setCoOwner = (i, name) => onChange({ coOwners: coOwners.map((n, k) => (k === i ? name : n)) });

    // Suggest the client while their name is not among the owners yet: fills the owner first, then adds a co-owner.
    const clientName = client?.name?.trim();
    const used = [values.ownerName, ...coOwners].some(n => n?.trim().toLowerCase() === clientName?.toLowerCase());
    const suggest = clientName && !used && (!values.ownerName?.trim() || coOwners.length < MAX_CO_OWNERS);
    const useClient = () => {
        if (!values.ownerName?.trim()) onChange({ ownerName: clientName });
        else {
            const empty = coOwners.findIndex(n => !n.trim());
            onChange({ coOwners: empty >= 0 ? coOwners.map((n, k) => (k === empty ? clientName : n)) : [...coOwners, clientName] });
        }
    };

    return (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
                {field('ownerName', t('jobs.fields.ownerName'), {
                    placeholder: t('survey.ownerHint'),
                    slotProps: { inputLabel: { shrink: true }, htmlInput: { maxLength: 150 } },
                })}
            </Grid>
            {coOwners.map((name, i) => (
                <Grid size={12} key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField fullWidth size="small" label={`${t('survey.coOwner')} ${i + 1}`} placeholder={t('survey.ownerHint')}
                               value={name} onChange={(e) => setCoOwner(i, e.target.value)} autoFocus={!name}
                               slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 150 } }} />
                    <IconButton size="small" aria-label={t('survey.removeCoOwner')}
                                onClick={() => onChange({ coOwners: coOwners.filter((_, k) => k !== i) })}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Grid>
            ))}
            <Grid size={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: -0.5 }}>
                {coOwners.length < MAX_CO_OWNERS && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ coOwners: [...coOwners, ''] })}>
                        {t('survey.addCoOwner')}
                    </Button>
                )}
                {suggest && (
                    <Button size="small" variant="outlined" startIcon={<PersonOutlineIcon />} onClick={useClient}
                            sx={{ maxWidth: '100%', '& .MuiButton-startIcon': { flexShrink: 0 } }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t('survey.useClient', { name: clientName })}
                        </Box>
                    </Button>
                )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('municipality', t('jobs.fields.municipality'), { slotProps: { htmlInput: { maxLength: 150 } } })}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('cadastralZone', t('jobs.fields.cadastralZone'), { slotProps: { htmlInput: { maxLength: 150 } } })}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                {field('propertySheet', t('jobs.fields.propertySheet'), {
                    placeholder: t('survey.sheetPlaceholder'),
                    slotProps: { inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } },
                })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                {field('parcelNumber', t('jobs.fields.parcelNumber'), {
                    placeholder: t('survey.parcelPlaceholder'),
                    slotProps: { inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } },
                })}
            </Grid>
        </Grid>
    );
}
